import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Brain,
  Upload,
  Database,
  Table,
  GitBranch,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  BarChart3,
  TrendingUp,
  Bookmark,
  HardDrive,
  Search,
  Settings,
  Shield,
  ScrollText,
  BellRing,
  Sliders,
  Trophy,
  Inbox,
  // Aliased — 'Map' shadows the JS built-in Map constructor; not used as a
  // constructor anywhere in this file, but the alias keeps the same
  // footgun-avoidance convention MapLibreChoropleth.jsx documents.
  Map as MapIcon,
  // Mobile off-canvas nav
  X
} from 'lucide-react'
import { cn } from '../utils/cn'
import BrandMark from './ui/BrandMark'
import { useAuth } from '../contexts/AuthContext'
import { buildSidebarSections, resolveActiveHref, collectLeafHrefs } from './sidebarNav.mjs'

// Nav data + the permission-filter/section-grouping logic live in
// sidebarNav.mjs as pure, framework-free data so they can be covered by a
// `node --test` regression suite (sidebarNav.test.mjs) that actually runs in
// CI — see that module for SECTION_META/NAV_ITEMS and the history of why
// this was extracted (the Administration/Settings group silently vanishing
// from the sidebar because it had no `section` key).
//
// Nav items there carry `icon` as a string key (e.g. 'LayoutDashboard')
// rather than a component, so this map resolves each one back to its
// lucide-react component for rendering. Every icon name used anywhere in
// sidebarNav.mjs's NAV_ITEMS must have an entry here.
//
// Settings IA rebuild (2026-07): the page-level icons that used to live
// here for Settings' 8 flat children (FileCode, ImageIcon, Bell,
// UsersIcon, Mail, MapPin) are gone — 'Settings' is now a single childless
// leaf, and those icons moved to SettingsLayout.jsx's own local ICONS map
// for its in-page L1/L2 nav instead.
const ICONS = {
  LayoutDashboard,
  BellRing,
  Brain,
  Upload,
  HardDrive,
  Search,
  Database,
  Table,
  GitBranch,
  BarChart3,
  TrendingUp,
  Bookmark,
  Settings,
  Shield,
  ScrollText,
  Sliders,
  Trophy,
  Inbox,
  Map: MapIcon,
}

const Sidebar = ({ mobileOpen = false, onMobileClose = () => {} }) => {
  const location = useLocation()
  // Only used to permission-filter the nav below (buildSidebarSections) —
  // the sidebar no longer owns an account menu of its own; that (and
  // logout) lives entirely in Header.jsx now. See sidebarNav.mjs for the
  // shared hasAnyPermission-based gate.
  const { user } = useAuth()

  // Permission-filter + section-grouping lives in sidebarNav.mjs (pure logic,
  // regression-tested under node --test). `requireAny: [...]` on a nav node
  // means "show iff user has at least one of these effective permissions." A
  // node without `requireAny` is always shown. See utils/permissions.js for
  // the super_admin bypass (shared with SettingsLayout so both use the exact
  // same rule).
  const sectionedNavigation = buildSidebarSections(user)
  const allNavItems = sectionedNavigation.flatMap(section => section.items)

  // The single most-specific leaf href for the current path — see
  // resolveActiveHref's doc comment in sidebarNav.mjs for why this can't be
  // NavLink's own `isActive`. Computed once per render and threaded through
  // every leaf below (top-level leaves, collapsed-rail icons, and group
  // children) instead of letting each NavLink decide independently.
  const activeHref = resolveActiveHref(location.pathname, collectLeafHrefs(allNavItems))

  const [expandedItems, setExpandedItems] = React.useState(
    allNavItems.filter(item =>
      item.children?.some(child => child.href === activeHref)
    ).map(item => item.name)
  )

  // Pinned-collapsed state persists in localStorage so the user's choice
  // survives reloads. When pinned-collapsed, the sidebar shrinks from
  // w-64 → w-16 by default; hovering on the rail for >500ms temporarily
  // expands it back to w-64, and the mouse leaving the sidebar
  // collapses it again. The explicit toggle button (top-right) flips
  // pinnedCollapsed without going through the hover delay.
  // Desktop-only concept — the mobile drawer is a separate open/closed
  // toggle owned by Layout (mobileOpen/onMobileClose below).
  const [pinnedCollapsed, setPinnedCollapsed] = React.useState(() => {
    try { return localStorage.getItem('iewrs.sidebar.collapsed') === '1' }
    catch { return false }
  })
  const [hoverExpanded, setHoverExpanded] = React.useState(false)
  const collapsed = pinnedCollapsed && !hoverExpanded

  const toggleCollapsed = () => {
    setPinnedCollapsed(prev => {
      const next = !prev
      try { localStorage.setItem('iewrs.sidebar.collapsed', next ? '1' : '0') } catch {}
      return next
    })
    // Clear any in-flight hover state so the toggle takes effect immediately.
    setHoverExpanded(false)
  }

  // Hover-to-expand: when pinned-collapsed, a sustained 500 ms hover
  // expands the sidebar. Leaving the sidebar collapses it again.
  const hoverTimer = React.useRef(null)
  const onMouseEnter = () => {
    if (!pinnedCollapsed) return
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => setHoverExpanded(true), 500)
  }
  const onMouseLeave = () => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null }
    setHoverExpanded(false)
  }
  React.useEffect(() => () => { if (hoverTimer.current) clearTimeout(hoverTimer.current) }, [])

  const toggleExpanded = (itemName) => {
    setExpandedItems(prev =>
      prev.includes(itemName)
        ? prev.filter(name => name !== itemName)
        : [...prev, itemName]
    )
  }

  // A parent group is "active" (expanded pill styling on its own header)
  // exactly when the globally-most-specific leaf (`activeHref`, computed
  // above) is one of its own children — not via any independent prefix
  // check, so a group can never light up for a route that actually belongs
  // to a sibling leaf/group elsewhere in the nav.
  const isParentActive = (item) => {
    return item.children?.some(child => child.href === activeHref)
  }

  // Mobile drawer a11y: Escape closes it, and body scroll is locked while
  // it's open so the page behind it can't scroll under the overlay.
  React.useEffect(() => {
    if (!mobileOpen) return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onMobileClose()
    }
    window.addEventListener('keydown', onKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [mobileOpen, onMobileClose])

  const closeButtonRef = React.useRef(null)
  React.useEffect(() => {
    if (mobileOpen) closeButtonRef.current?.focus()
  }, [mobileOpen])

  // Close the mobile drawer after a navigation so the next screen isn't
  // hidden behind it. No-op on desktop (mobileOpen is always false there).
  const handleNavClick = () => {
    if (mobileOpen) onMobileClose()
  }

  // Below `lg` the closed drawer is only hidden visually (translate-x-full)
  // — its links stay mounted for the open/close transition. Without this,
  // Tab from the top of the page walks straight into the invisible
  // off-screen drawer before ever reaching the visible hamburger button.
  // `inert` removes the whole subtree from the tab order (and from screen
  // readers) whenever it's off-screen; it's a no-op at `lg`+, where the
  // rail is always on-screen regardless of `mobileOpen`.
  const [isDesktop, setIsDesktop] = React.useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  ))
  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const handler = (e) => setIsDesktop(e.matches)
    if (mq.addEventListener) mq.addEventListener('change', handler)
    else mq.addListener(handler)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler)
      else mq.removeListener(handler)
    }
  }, [])
  // Auto-close the mobile drawer if the viewport crosses into desktop while
  // it's open — the close button and scrim are both `lg:hidden`, so nothing
  // else can clear `mobileOpen` (and its body-scroll lock) once that happens.
  React.useEffect(() => {
    if (isDesktop && mobileOpen) onMobileClose()
  }, [isDesktop, mobileOpen, onMobileClose])

  const drawerInert = !isDesktop && !mobileOpen
  // Spread so we only ever emit the attribute when true — passing
  // `inert={false}` would render `inert="false"`, and HTML boolean
  // attributes are true merely by being present, value notwithstanding.
  const inertProps = drawerInert ? { inert: '' } : {}

  // Renders one top-level nav item (leaf or parent-with-children), shared by
  // every section. Active state is a soft tinted pill (primary-50 fill /
  // primary-700 text / a quiet primary-100 ring) rather than a saturated
  // solid block — still unmistakably "current page" via fill + text weight
  // + icon colour (not colour alone), with a crisp focus-visible ring.
  const renderNavItem = (item) => {
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems.includes(item.name)
    const parentActive = isParentActive(item)
    const Icon = ICONS[item.icon]

    if (hasChildren) {
      // When the sidebar is collapsed we render the parent as a single icon
      // (no children dropdown). Clicking it expands the sidebar first and
      // opens the group — keeps mouse-on-icon navigation predictable.
      // (Collapsed rail is desktop-only — the mobile drawer always renders
      // the full expandable list regardless of the desktop collapse
      // preference, via the lg: breakpoint pairing below.)
      return (
        <div key={item.name}>
          {/* Collapsed rail (desktop only) — icon-only trigger */}
          {collapsed && (
            <button
              onClick={() => { toggleCollapsed(); if (!isExpanded) toggleExpanded(item.name) }}
              title={item.name}
              aria-label={item.name}
              className={cn(
                'hidden lg:flex w-full min-h-[44px] items-center justify-center p-2.5 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
                parentActive ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-100' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              )}
            >
              <Icon className={cn('w-5 h-5', parentActive ? 'text-primary-600' : 'text-gray-500')} strokeWidth={1.75} />
            </button>
          )}

          <div className={collapsed ? 'lg:hidden' : undefined}>
          <button
            onClick={() => toggleExpanded(item.name)}
            aria-expanded={isExpanded}
            className={cn(
              'w-full min-h-[44px] flex items-center justify-between px-4 py-2.5 text-sm font-medium rounded-full transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
              parentActive
                ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-100 font-semibold'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            )}
          >
            <div className="flex items-center space-x-3">
              <Icon className={cn(
                'w-5 h-5',
                parentActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
              )} strokeWidth={1.75} />
              <span>{item.name}</span>
            </div>
            <ChevronRight className={cn(
              'w-4 h-4 transition-transform duration-200',
              isExpanded ? 'rotate-90' : '',
              parentActive ? 'text-primary-600' : 'text-gray-400'
            )} strokeWidth={1.75} />
          </button>

          {isExpanded && (
            <div className="mt-1 ml-4 pl-3 space-y-0.5 border-l border-gray-200">
              {item.children.map((child) => {
                const childActive = child.href === activeHref
                const ChildIcon = ICONS[child.icon]
                return (
                  // Plain `Link`, not `NavLink`, deliberately: NavLink computes
                  // its own `aria-current` from its own (non-specificity-aware)
                  // isActive, and that computed value can't be suppressed by
                  // any prop we pass in — passing `undefined` just falls back
                  // to NavLink's default `"page"`, which would put
                  // `aria-current="page"` back on the less-specific sibling
                  // for the exact descendant-route case this fix targets. A
                  // plain Link lets `childActive` (already specificity-aware
                  // via `activeHref`) drive both the class and aria-current
                  // with no second, conflicting source of truth.
                  <Link
                    key={child.href}
                    to={child.href}
                    onClick={handleNavClick}
                    aria-current={childActive ? 'page' : undefined}
                    className={cn(
                      'flex items-center min-h-[44px] space-x-3 px-4 py-2 text-sm font-medium rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
                      childActive
                        ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-100 font-semibold'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )}
                  >
                    <ChildIcon className={cn('w-4 h-4 shrink-0', childActive ? 'text-primary-600' : 'text-gray-400')} strokeWidth={1.75} />
                    <span>{child.name}</span>
                  </Link>
                )
              })}
            </div>
          )}
          </div>
        </div>
      )
    }

    // Most-specific-leaf-wins: this item is active iff it IS the one
    // `activeHref` resolved to, not "am I an ancestor of the current path"
    // (NavLink's own default `isActive`, which is what let a leaf like
    // `/app/alerts` light up alongside `/app/alerts/detector`).
    const leafActive = item.href === activeHref

    // Collapsed rail (desktop only) — the same leaf/parent set, rendered as
    // icon-only circles. Inactive icon uses gray-500 (4.83:1 on white) instead of
    // gray-400 (2.54:1) to meet WCAG AA 3:1 minimum for UI components, since the
    // icon is the sole visual cue (no adjacent label).
    //
    // Plain `Link`, not `NavLink` — see the comment on the group-children
    // Link above: NavLink's own `aria-current` is gated on its own
    // (non-specificity-aware) isActive and can't be suppressed via props,
    // so a plain Link driven entirely by `leafActive` is the only way to
    // guarantee aria-current lands on the truly-active leaf only.
    const collapsedIconButton = hasChildren ? null : (
      <Link
        key={`${item.href}-rail`}
        to={item.href}
        title={item.name}
        aria-label={item.name}
        aria-current={leafActive ? 'page' : undefined}
        className={cn(
          'hidden lg:flex w-full min-h-[44px] items-center justify-center p-2.5 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
          leafActive ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-100' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
        )}
      >
        <Icon className={cn('w-5 h-5', leafActive ? 'text-primary-600' : 'text-gray-500')} strokeWidth={1.75} />
      </Link>
    )

    // Leaf nav item — active state is a soft tinted pill (see helper
    // comment above); inactive is gray text with a soft gray hover tint.
    return (
      <React.Fragment key={item.href}>
        {collapsed && collapsedIconButton}
        <Link
          to={item.href}
          onClick={handleNavClick}
          aria-current={leafActive ? 'page' : undefined}
          className={cn(
            'flex items-center min-h-[44px] space-x-3 px-4 py-2.5 text-sm font-medium rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2',
            collapsed && 'lg:hidden',
            leafActive
              ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-100 font-semibold'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
          )}
        >
          <Icon className={cn('w-5 h-5 shrink-0', leafActive ? 'text-primary-600' : 'text-gray-400')} strokeWidth={1.75} />
          <span>{item.name}</span>
        </Link>
      </React.Fragment>
    )
  }

  return (
    <>
      {/* Mobile scrim — sits below the drawer (z-40 < z-50), click-to-close.
          Desktop never renders this (mobileOpen only flips true < lg). */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-900/50 backdrop-blur-[2px] lg:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role={mobileOpen ? 'dialog' : undefined}
        aria-modal={mobileOpen ? 'true' : undefined}
        aria-label={mobileOpen ? 'Navigation menu' : undefined}
        {...inertProps}
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200/80 transition-all duration-200 ease-out',
          'w-72',
          collapsed ? 'lg:w-16' : 'lg:w-64',
          mobileOpen ? 'translate-x-0 shadow-elev-3' : '-translate-x-full',
          'lg:translate-x-0',
          // When hover-expanded over a pinned-collapsed rail, float the
          // expanded sidebar above the page content with a shadow so it
          // doesn't push the rest of the layout around. Otherwise the
          // desktop sidebar is a normal, static flex child.
          pinnedCollapsed && hoverExpanded
            ? 'lg:absolute lg:top-0 lg:left-0 lg:h-full lg:z-40 lg:shadow-elev-3'
            : 'lg:static lg:z-auto'
        )}
      >
        {/* Logo + collapse toggle */}
        <div className="flex items-center h-16 px-2 border-b border-gray-200/80 relative">
          <div className={cn(
            'flex items-center flex-1',
            collapsed ? 'lg:justify-center' : 'justify-start pl-2 space-x-2.5'
          )}>
            <BrandMark
              iconClass="h-8"
              wordmarkClass="text-lg"
              wordmarkWrapClass={cn(collapsed && 'lg:hidden')}
            />
          </div>

          {/* Mobile close button — replaces the desktop collapse toggle
              below lg. Focused automatically when the drawer opens. */}
          <button
            ref={closeButtonRef}
            onClick={onMobileClose}
            aria-label="Close navigation menu"
            className="flex h-11 w-11 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 lg:hidden"
          >
            <X className="w-5 h-5" strokeWidth={1.75} />
          </button>

          {/* Desktop collapse toggle — a small 24px chip on a 44px hit area
              so the control still meets the touch-target minimum. */}
          <button
            onClick={toggleCollapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="hidden lg:flex absolute -right-5 top-1/2 -translate-y-1/2 h-11 w-11 items-center justify-center text-gray-500 transition-colors hover:text-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 rounded-full"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white border border-gray-200 shadow-elev-1 hover:border-primary-200 transition-colors">
              {collapsed ? <ChevronsRight className="w-3.5 h-3.5" strokeWidth={1.75} /> : <ChevronsLeft className="w-3.5 h-3.5" strokeWidth={1.75} />}
            </span>
          </button>
        </div>

        {/* Navigation — grouped into labelled sections: small uppercase
            micro-labels turning a flat list into sections. Active leaf item
            renders as a soft tinted pill, not a saturated solid block — see
            renderNavItem above. Nav items are chip/pill-shaped, so
            rounded-full throughout. */}
        <nav className={cn('flex-1 py-3 overflow-y-auto', collapsed ? 'px-2 lg:px-2' : 'px-3')}>
          {sectionedNavigation.map((section, index) => (
            <div key={section.key} className={index > 0 ? 'mt-4' : undefined}>
              <div className={cn('pb-1.5', collapsed && 'lg:hidden')}>
                <p className="nav-group-label">{section.label}</p>
              </div>
              {collapsed && index > 0 && (
                <div className="hidden lg:block mx-2 my-2 border-t border-gray-100" aria-hidden="true" />
              )}
              <div className="space-y-1">
                {section.items.map(renderNavItem)}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom padding so the last nav item never sits flush against the
            rail's rounded bottom edge — this used to be provided
            incidentally by the System Online footer + user menu below,
            both of which relocated out of the sidebar: status → the
            FloatingSystemStatus widget (Layout.jsx), user menu → Header's
            own account menu (same useAuth().logout() + /app/account
            actions, now with parity plus a couple of extra gated links). */}
        <div className={cn(collapsed ? 'h-2' : 'h-3')} aria-hidden="true" />
      </div>
    </>
  )
}

export default Sidebar
